/**
 * Supabase認証サービス
 * 認証関連の機能を提供
 */

import { SupabaseClient } from '@supabase/supabase-js';
import { getSupabaseClient } from '../../utils/supabase';
import { User, Role } from '../../types';
import { AuthService, DataServiceError, ErrorCodes } from '../dataService';
import { isDemoMode } from '../../utils/environment';
import logger from '../../utils/logger';

// 認証状態保存用のlocalStorageキー
export const AUTH_STORAGE_KEY = 'fleetpilot_mock_current_user';

export class SupabaseAuthService implements AuthService {
  public supabase: SupabaseClient | null = null;
  private authStateChangeCallback?: (user: User | null) => void;
  public isDemo: boolean;

  constructor(demoMode?: boolean) {
    this.isDemo = demoMode ?? isDemoMode();

    // 全モードでSupabaseクライアントを初期化（ログイン機能用）
    this.initializeSupabase();

    // リスナーは本番モードのみ設定
    if (!this.isDemo) {
      this.setupAuthListener();
    } else {
      logger.debug('✅ SupabaseAuthService: デモモードで初期化（Supabaseクライアント利用可能）');
    }
  }

  private initializeSupabase() {
    try {
      // シングルトンSupabaseクライアントを使用
      this.supabase = getSupabaseClient();

      if (!this.supabase) {
        logger.warn('Supabaseの設定が見つかりません。環境変数を確認してください。');
        return;
      }

      logger.debug('✅ SupabaseAuthService: シングルトンクライアント取得完了');
    } catch (error) {
      logger.error('❌ Supabase initialization failed:', error);
    }
  }

  async loginWithRole(role: Role): Promise<User> {
    // デモモード時は常にデモ用ログインを使用
    if (this.isDemo || !this.supabase) {
      logger.debug('🎭 SupabaseAuthService: デモモードまたはSupabase未設定 - デモログイン実行');
      return this.loginWithRoleDemo(role);
    }

    throw new DataServiceError(
      'Supabase認証機能は実装中です。現在はデモ用ログインをご利用ください。',
      ErrorCodes.AUTHENTICATION_FAILED
    );
  }

  private async loginWithRoleDemo(role: Role): Promise<User> {
    // Supabaseベースのデモ用ログイン機能（リモートデータ使用）
    const demoUsers = {
      [Role.SUPER_ADMIN]: {
        name: 'ラクハイ管理者デモ',
        company: 'ラクハイ本社',
        companyId: 'rakuhai-headquarters-demo',
      },
      [Role.ADMIN]: {
        name: 'サンプル管理者',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259', // デモ会社ID統一
      },
      [Role.DISPATCHER]: {
        name: 'サンプル配車担当',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
      },
      [Role.DRIVER]: {
        name: 'サンプル陸送員A',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
        landTransporterId: 'demo-driver-1',
      },
      [Role.LAND_TRANSPORTER]: {
        name: 'サンプル陸送員B',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
        landTransporterId: '0b00ad35-df7c-4347-a02e-0b7b378d83e4',
      },
      [Role.MANAGER]: {
        name: 'サンプル管理者（マネージャー）',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
      },
      [Role.READ_ONLY]: {
        name: '閲覧専用デモ',
        company: '株式会社サンプル陸送',
        companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
      },
    };

    const userData = demoUsers[role];
    const user: User = {
      id: crypto.randomUUID(),
      name: userData.name,
      role,
      company: userData.company,
      companyId: userData.companyId,
      landTransporterId: 'landTransporterId' in userData ? userData.landTransporterId : undefined,
    };

    // デモモード時もSupabaseのデモデータを使用するため、localStorage認証は使用しない
    if (this.isDemo) {
      logger.debug('🎭 SupabaseAuthService: デモモード認証完了', {
        role,
        companyId: user.companyId,
        name: user.name,
      });
    } else {
      // 非デモモード時のlocalStorage保存（従来の動作維持）
      try {
        localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
      } catch (error) {
        logger.warn('Failed to save auth state to localStorage:', error);
      }
    }

    return user;
  }

  async loginWithEmail(email: string, password: string): Promise<User> {
    // 1. Supabase認証を最優先で試行（実際のアカウント用）
    if (this.supabase) {
      logger.debug('🔍 loginWithEmail: Supabase認証を試行（実際のアカウント）');

      try {
        // Supabase認証実行
        const { data, error } = await this.supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (!error && data.user) {
          logger.debug('✅ Supabase認証成功:', { email, userId: data.user.id });

          // ユーザープロファイル取得
          const userProfile = await this.getUserProfile(data.user.id);
          if (userProfile) {
            // 会社の利用可否チェック
            const isActive = await this.checkCompanyActive(userProfile.company_id);
            if (!isActive) {
              // 利用停止中の場合、ログアウトしてエラーを返す
              await this.supabase.auth.signOut();
              logger.warn('⚠️ 会社が利用停止中:', {
                email,
                companyId: userProfile.company_id,
              });

              throw new DataServiceError(
                'ご利用の陸送会社はシステム利用が停止されています。詳細は管理者にお問い合わせください。',
                ErrorCodes.COMPANY_INACTIVE
              );
            }

            // Userオブジェクトに変換
            const user: User = {
              id: data.user.id,
              email: email,
              name: userProfile.name,
              role: this.mapDatabaseRoleToEnum(userProfile.role),
              company: userProfile.company_id ? '会社名取得中...' : 'ラクハイ本社',
              companyId: userProfile.company_id,
              landTransporterId: userProfile.land_transporter_id,
            };

            // 認証成功時のlocalStorage保存
            try {
              localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
              logger.debug('✅ 認証状態をlocalStorageに保存');
            } catch (storageError) {
              logger.warn('認証状態の保存に失敗:', storageError);
            }
            return user;
          }
        }

        // Supabase認証が失敗した場合、エラーをログに記録して続行
        if (error) {
          logger.warn('⚠️ Supabase認証失敗:', error.message);
        }
      } catch (supabaseError) {
        logger.warn('⚠️ Supabase認証エラー:', supabaseError);
      }
    } else {
      logger.warn('⚠️ Supabaseクライアントが初期化されていません');
    }

    // 2. Supabaseが利用できない場合のフォールバック認証
    if (!this.supabase) {
      logger.warn('⚠️ フォールバック認証を試行');
      return this.fallbackEmailAuth(email, password);
    }

    // 3. 高速ログイン: localStorage認証確認（既にSupabase認証済みの場合はスキップ）
    try {
      const storedUserData = localStorage.getItem(AUTH_STORAGE_KEY);
      if (storedUserData) {
        const cachedUser: User = JSON.parse(storedUserData);
        if (cachedUser && cachedUser.name && cachedUser.role && cachedUser.email === email) {
          logger.debug('🚀 loginWithEmail: localStorage高速ログイン使用', {
            name: cachedUser.name,
            role: cachedUser.role,
          });

          // バックグラウンドでSupabase認証検証（ネットワークエラーは無視）
          if (this.supabase) {
            setTimeout(async () => {
              try {
                await this.supabase!.auth.signInWithPassword({ email, password });
                logger.debug('✅ loginWithEmail: バックグラウンドSupabase認証完了');
              } catch (bgError) {
                logger.warn('⚠️ loginWithEmail: バックグラウンド認証失敗（継続使用可）:', bgError);
              }
            }, 100);
          }

          return cachedUser; // 即座に認証状態返却
        }
      }
    } catch (parseError) {
      logger.warn('localStorage認証データ解析失敗:', parseError);
      localStorage.removeItem(AUTH_STORAGE_KEY);
    }

    // 4. 最終的にエラーを返す
    throw new DataServiceError(
      'メールアドレスまたはパスワードが正しくありません',
      ErrorCodes.AUTHENTICATION_FAILED
    );
  }

  /**
   * Supabase接続エラー時のフォールバック認証
   * デモ用の認証情報でログインを試行
   */
  private async fallbackEmailAuth(email: string, password: string): Promise<User> {
    logger.debug('🔄 フォールバック認証開始:', { email });

    // デモ用認証情報（環境変数から取得）
    const demoPassword = import.meta.env.VITE_DEMO_PASSWORD || 'demo-password-2024';
    const demoUsers = {
      'admin@sample-rikuso.co.jp': {
        password: demoPassword,
        user: {
          id: 'demo-admin-001',
          email: 'admin@sample-rikuso.co.jp',
          name: 'サンプル管理者',
          role: Role.ADMIN,
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
        } as User,
      },
      'dispatcher@sample-rikuso.co.jp': {
        password: demoPassword,
        user: {
          id: 'demo-dispatcher-001',
          email: 'dispatcher@sample-rikuso.co.jp',
          name: 'サンプル配車担当',
          role: Role.DISPATCHER,
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
        } as User,
      },
      'driver-c@sample-rikuso.co.jp': {
        password: demoPassword,
        user: {
          id: '4cb5db58-15ef-4147-9a8e-978e0bdd1aa6',
          email: 'driver-c@sample-rikuso.co.jp',
          name: 'サンプル陸送員C',
          role: Role.DRIVER,
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
          landTransporterId: 'a998fdf5-4933-4780-8c02-509035a32b93',
        } as User,
      },
      'readonly@sample-rikuso.co.jp': {
        password: demoPassword,
        user: {
          id: 'demo-readonly-001',
          email: 'readonly@sample-rikuso.co.jp',
          name: 'サンプル閲覧者',
          role: Role.READ_ONLY,
          company: '株式会社サンプル陸送',
          companyId: 'e32a0499-6456-44ac-9b16-b0001098c259',
        } as User,
      },
    };

    const demoCredential = demoUsers[email as keyof typeof demoUsers];

    if (!demoCredential) {
      throw new DataServiceError(
        'このメールアドレスは登録されていません',
        ErrorCodes.AUTHENTICATION_FAILED
      );
    }

    if (demoCredential.password !== password) {
      throw new DataServiceError('パスワードが正しくありません', ErrorCodes.AUTHENTICATION_FAILED);
    }

    logger.debug('✅ フォールバック認証成功:', {
      email,
      name: demoCredential.user.name,
      role: demoCredential.user.role,
    });

    // フォールバック認証成功時もlocalStorageに保存
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(demoCredential.user));
      logger.debug('✅ フォールバック認証状態をlocalStorageに保存');
    } catch (storageError) {
      logger.warn('フォールバック認証状態の保存に失敗:', storageError);
    }

    return demoCredential.user;
  }

  async logout(): Promise<void> {
    try {
      // 1. Supabaseログアウト（Supabaseが利用可能な場合）
      if (this.supabase) {
        const { error } = await this.supabase.auth.signOut();
        if (error) {
          throw new DataServiceError(
            `ログアウトに失敗しました: ${error.message}`,
            ErrorCodes.AUTHENTICATION_FAILED
          );
        }
        logger.debug('Supabaseログアウト完了');
      }

      // 2. localStorageから認証状態を削除（デモモード・リロード対応）
      try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
        logger.debug('localStorage認証状態削除完了');
      } catch (storageError) {
        logger.warn('Failed to clear auth state from localStorage:', storageError);
        // localStorageエラーは警告のみで処理続行
      }
    } catch (error) {
      logger.error('Logout error:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'ログアウトに失敗しました',
        ErrorCodes.AUTHENTICATION_FAILED,
        error as Error
      );
    }
  }

  async getCurrentUser(): Promise<User | null> {
    // 1. Supabase認証の確認（Supabaseが利用可能な場合）
    if (this.supabase) {
      try {
        logger.debug('🔍 getCurrentUser: Supabase認証ユーザー取得開始');

        const {
          data: { user },
          error,
        } = await this.supabase.auth.getUser();

        logger.debug('🔍 getCurrentUser auth.getUser結果:', {
          user: user ? { id: user.id, email: user.email } : null,
          error: error?.message,
        });

        if (!error && user) {
          logger.debug('🔍 getCurrentUser: ユーザープロファイル取得開始:', user.id);

          try {
            // ユーザープロファイル取得
            const userProfile = await this.getUserProfile(user.id);

            logger.debug('🔍 getCurrentUser: プロファイル取得成功:', userProfile);

            const supabaseUser: User = {
              id: user.id,
              name: userProfile.name,
              role: userProfile.role as Role,
              company: userProfile.company_id || 'ラクハイ本社', // 会社情報は後でcompaniesテーブルから取得
              companyId: userProfile.company_id,
              landTransporterId: userProfile.land_transporter_id,
            };

            // Supabase認証成功時もlocalStorageに保存（リロード対応）
            try {
              localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(supabaseUser));
              logger.debug('✅ getCurrentUser: Supabase認証状態をlocalStorageに保存');
            } catch (storageError) {
              logger.warn('Failed to save Supabase auth state to localStorage:', storageError);
            }

            return supabaseUser;
          } catch (profileError) {
            logger.error('❌ ユーザープロファイル取得エラー:', profileError);
            // プロファイル取得エラーの場合もlocalStorageフォールバックを試行
          }
        } else {
          if (error) {
            logger.error('❌ Supabase認証確認エラー:', error.message);
          } else {
            logger.debug('🔍 getCurrentUser: Supabaseユーザーなし');
          }
        }
      } catch (supabaseError) {
        logger.error('❌ Supabase getCurrentUser error:', supabaseError);
      }
    } else {
      logger.debug('🔍 getCurrentUser: Supabaseクライアントなし');
    }

    // 2. localStorageからの認証状態復元（フォールバック・デモモード対応）
    try {
      logger.debug('🔍 getCurrentUser: localStorage復元を試行');
      const storedUserData = localStorage.getItem(AUTH_STORAGE_KEY);

      if (storedUserData) {
        const user: User = JSON.parse(storedUserData);

        // データ整合性チェック
        if (user && user.name && user.role) {
          logger.debug('✅ getCurrentUser: localStorage認証状態復元成功:', {
            name: user.name,
            role: user.role,
            company: user.company,
          });
          return user;
        } else {
          logger.warn('⚠️ getCurrentUser: localStorage認証データが不正:', user);
          // 不正なデータの場合は削除
          localStorage.removeItem(AUTH_STORAGE_KEY);
        }
      } else {
        logger.debug('🔍 getCurrentUser: localStorageに認証データなし');
      }
    } catch (storageError) {
      logger.error('❌ localStorage認証状態復元エラー:', storageError);
      // localStorageエラーの場合はクリア
      try {
        localStorage.removeItem(AUTH_STORAGE_KEY);
      } catch (clearError) {
        logger.warn('localStorage clear failed:', clearError);
      }
    }

    // 3. どちらも失敗した場合
    logger.debug('🔍 getCurrentUser: 認証ユーザーが見つかりません');
    return null;
  }

  private async getUserProfile(userId: string, retryCount: number = 0): Promise<any> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }

    const isRetry = retryCount > 0;
    const logPrefix = isRetry ? `🔄 getUserProfile (retry ${retryCount})` : '🔍 getUserProfile';

    logger.debug(`${logPrefix}: クエリ実行開始`, {
      userId,
      table: 'user_profiles',
      retryCount,
      timestamp: new Date().toISOString(),
    });

    try {
      // Supabase接続テスト（初回のみ）
      if (retryCount === 0) {
        const healthCheck = await this.supabase.from('user_profiles').select('count').limit(1);
        logger.debug(`${logPrefix}: 接続テスト結果`, {
          success: !healthCheck.error,
          error: healthCheck.error?.message,
          status: healthCheck.status,
        });
      }

      // タイムアウト付きクエリの実装（短縮版）
      const queryPromise = this.supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      const timeoutDuration = retryCount === 0 ? 3000 : 5000; // 初回3秒、リトライ5秒
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => {
          reject(new Error(`getUserProfile query timeout after ${timeoutDuration / 1000} seconds`));
        }, timeoutDuration);
      });

      logger.debug(`${logPrefix}: タイムアウト付きクエリ開始（${timeoutDuration / 1000}秒）`);

      const { data, error } = (await Promise.race([queryPromise, timeoutPromise])) as any;

      logger.debug(`${logPrefix}: クエリ完了`, {
        hasData: !!data,
        error: error?.message,
        errorCode: error?.code,
        errorDetails: error?.details,
        timestamp: new Date().toISOString(),
      });

      if (error) {
        logger.error(`❌ ${logPrefix} エラー詳細:`, {
          message: error.message,
          code: error.code,
          details: error.details,
          hint: error.hint,
          timestamp: new Date().toISOString(),
        });

        // PGRST116 = テーブルが見つからない、PGRST301 = RLSで拒否された場合
        if (error.code === 'PGRST116') {
          throw new DataServiceError(
            'user_profilesテーブルが存在しません。データベースの移行を確認してください。',
            ErrorCodes.DATA_ACCESS_ERROR
          );
        }

        if (error.code === 'PGRST301' || error.message.includes('RLS')) {
          throw new DataServiceError(
            'ユーザープロファイルへのアクセスが拒否されました。権限設定を確認してください。',
            ErrorCodes.AUTHENTICATION_FAILED
          );
        }

        // リトライ可能なエラーかチェック
        if (retryCount === 0 && this.shouldRetryGetUserProfile(error)) {
          logger.warn(`⚠️ ${logPrefix}: リトライ可能なエラー、1秒後にリトライします`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // 1秒待機
          return this.getUserProfile(userId, retryCount + 1);
        }

        throw new DataServiceError(
          `ユーザープロファイルの取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      if (!data) {
        logger.warn(`⚠️ ${logPrefix}: データが存在しません`, { userId });

        // データなしの場合もリトライ対象
        if (retryCount === 0) {
          logger.warn(`⚠️ ${logPrefix}: データ未存在、1秒後にリトライします`);
          await new Promise(resolve => setTimeout(resolve, 1000)); // 1秒待機
          return this.getUserProfile(userId, retryCount + 1);
        }

        throw new DataServiceError(
          'ユーザープロファイルが見つかりません',
          ErrorCodes.RESOURCE_NOT_FOUND
        );
      }

      logger.debug(`✅ ${logPrefix}: 成功`, {
        userId: data.id,
        role: data.role,
        company_id: data.company_id,
        retryCount,
        timestamp: new Date().toISOString(),
      });

      return data;
    } catch (error: any) {
      logger.error(`❌ ${logPrefix}: 予期しないエラー:`, {
        error: error.message,
        stack: error.stack,
        userId,
        retryCount,
        timestamp: new Date().toISOString(),
      });

      if (error instanceof DataServiceError) {
        throw error;
      }

      // タイムアウトエラーのリトライ判定
      if (error.message.includes('timeout')) {
        if (retryCount === 0) {
          logger.warn(`⚠️ ${logPrefix}: タイムアウト、2秒後にリトライします`);
          await new Promise(resolve => setTimeout(resolve, 2000)); // 2秒待機
          return this.getUserProfile(userId, retryCount + 1);
        }

        throw new DataServiceError(
          'ユーザープロファイルの取得がタイムアウトしました。ネットワーク接続またはデータベースの状態を確認してください。',
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      throw new DataServiceError(
        `ユーザープロファイルの取得中に予期しないエラーが発生しました: ${error.message}`,
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }
  }

  /**
   * 会社のシステム利用可否をチェック（FL-184）
   * subscription_statusを使用して判定
   * @param companyId - 会社ID
   * @returns 会社がシステム利用可能な場合true、それ以外false
   */
  private async checkCompanyActive(companyId: string | undefined): Promise<boolean> {
    // companyIdが未設定（スーパー管理者など）の場合は常に許可
    if (!companyId) {
      logger.debug('✅ checkCompanyActive: companyIdなし（スーパー管理者）- アクセス許可');
      return true;
    }

    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }

    logger.debug('🔍 checkCompanyActive: 会社の利用可否確認開始', { companyId });

    try {
      const { data, error } = await this.supabase
        .from('companies')
        .select('subscription_status')
        .eq('id', companyId)
        .maybeSingle();

      if (error) {
        logger.error('❌ checkCompanyActive: クエリエラー', {
          message: error.message,
          code: error.code,
          companyId,
        });

        throw new DataServiceError(
          `会社情報の取得に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      if (!data) {
        logger.warn('⚠️ checkCompanyActive: 会社情報が見つかりません', { companyId });
        return false;
      }

      // active または trial の場合のみシステム利用可能
      const isActive =
        data.subscription_status === 'active' || data.subscription_status === 'trial';

      logger.debug('✅ checkCompanyActive: 確認完了', {
        companyId,
        subscriptionStatus: data.subscription_status,
        isActive,
      });

      return isActive;
    } catch (error: any) {
      logger.error('❌ checkCompanyActive: 予期しないエラー', {
        error: error.message,
        companyId,
      });

      if (error instanceof DataServiceError) {
        throw error;
      }

      throw new DataServiceError(
        `会社の利用可否確認中にエラーが発生しました: ${error.message}`,
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }
  }

  /**
   * データベースのrole値をUI用Role enumに変換
   */
  private mapDatabaseRoleToEnum(dbRole: string): Role {
    const roleMapping: Record<string, Role> = {
      super_admin: Role.SUPER_ADMIN,
      ラクハイ管理者: Role.SUPER_ADMIN,
      admin: Role.ADMIN,
      陸送会社管理者: Role.ADMIN,
      dispatcher: Role.DISPATCHER,
      '配車・営業担当者': Role.DISPATCHER,
      driver: Role.DRIVER,
      陸送員: Role.DRIVER,
      manager: Role.MANAGER,
      マネージャー: Role.MANAGER,
      read_only: Role.READ_ONLY,
      閲覧専用: Role.READ_ONLY,
    };

    const mappedRole = roleMapping[dbRole];
    if (!mappedRole) {
      logger.warn('🔧 Unknown role from database, defaulting to read_only:', dbRole);
      return Role.READ_ONLY;
    }

    logger.debug('🔧 Role mapping:', { dbRole, mappedRole });
    return mappedRole;
  }

  // getUserProfileのリトライ判定
  private shouldRetryGetUserProfile(error: any): boolean {
    // ネットワーク関連エラー、一時的なデータベースエラーをリトライ対象とする
    if (!error) return false;

    const retryableCodes = [
      'PGRST500', // Internal Server Error
      'PGRST503', // Service Unavailable
      '23505', // unique_violation (一時的な競合)
    ];

    const retryableMessages = ['network', 'connection', 'timeout', 'temporary', 'unavailable'];

    // エラーコードでの判定
    if (error.code && retryableCodes.includes(error.code)) {
      return true;
    }

    // エラーメッセージでの判定
    if (error.message) {
      const message = error.message.toLowerCase();
      return retryableMessages.some(keyword => message.includes(keyword));
    }

    return false;
  }

  // 新規ユーザー登録
  async signUp(
    email: string,
    password: string,
    userData: {
      name: string;
      role: Role;
      companyId?: string; // super_adminの場合undefinedを許可
      landTransporterId?: string;
    }
  ): Promise<User> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.AUTHENTICATION_FAILED
      );
    }

    try {
      logger.debug('🚀 signUp開始:', { email, userData });

      // Supabaseユーザー作成
      logger.debug('🔍 Supabase auth.signUp呼び出し前');
      const { data, error } = await this.supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            role: userData.role,
            company_id: userData.companyId,
            land_transporter_id: userData.landTransporterId,
          },
        },
      });

      logger.debug('🔍 Supabase auth.signUp レスポンス:', {
        user: data.user?.id,
        error,
        session: data.session ? 'セッションあり' : 'セッションなし',
      });

      if (error) {
        logger.error('❌ Auth SignUp error:', error);
        throw new DataServiceError(
          `ユーザー登録に失敗しました: ${error.message}`,
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      if (!data.user) {
        logger.error('❌ No user returned from auth.signUp');
        throw new DataServiceError('ユーザー作成に失敗しました', ErrorCodes.AUTHENTICATION_FAILED);
      }

      logger.debug('🔍 ユーザープロファイル作成開始:', data.user.id);

      // ユーザープロファイル作成
      await this.createUserProfile(data.user.id, userData);

      logger.debug('✅ signUp完了');

      return {
        id: data.user.id,
        name: userData.name,
        role: userData.role,
        company:
          userData.role === Role.SUPER_ADMIN ? 'ラクハイ本社' : userData.companyId || '未設定',
        companyId: userData.role === Role.SUPER_ADMIN ? undefined : userData.companyId,
        landTransporterId: userData.landTransporterId,
      };
    } catch (error) {
      logger.error('Supabase signup error:', error);
      logger.error('SignUp userData:', userData);
      logger.error(
        'Processed companyId:',
        userData.role === Role.SUPER_ADMIN ? null : userData.companyId || null
      );

      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        `ユーザー登録に失敗しました: ${error instanceof Error ? error.message : 'Unknown error'}`,
        ErrorCodes.AUTHENTICATION_FAILED,
        error as Error
      );
    }
  }

  // ユーザープロファイル作成（カスタムクレーム付き）
  private async createUserProfile(
    userId: string,
    userData: {
      name: string;
      role: Role;
      companyId?: string; // super_adminの場合undefinedを許可
      landTransporterId?: string;
    }
  ): Promise<void> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }

    logger.debug('🔍 createUserProfile開始:', {
      userId,
      userData,
      role: userData.role,
    });

    // データベース関数を使用してプロファイル作成とメタデータ設定を同時実行
    // super_adminの場合はcompany_idをnullに設定
    const companyId = userData.role === Role.SUPER_ADMIN ? null : userData.companyId || null;

    logger.debug('🔍 RPC関数呼び出し前:', {
      user_id: userId,
      user_name: userData.name,
      user_role: userData.role,
      company_id: companyId,
      land_transporter_id: userData.landTransporterId,
    });

    try {
      // まず、関数の存在を確認
      logger.debug('🔍 データベース関数を呼び出し中...');

      const { data, error } = await this.supabase.rpc('create_user_profile_with_metadata', {
        user_id: userId,
        user_name: userData.name,
        user_role: userData.role,
        company_id: companyId,
        land_transporter_id: userData.landTransporterId,
      });

      logger.debug('🔍 RPC関数レスポンス:', { data, error });

      if (error) {
        logger.error('❌ Database function error:', error);

        // 関数が存在しない場合の代替実装
        if (error.message?.includes('function') && error.message?.includes('does not exist')) {
          logger.debug('⚠️ 関数が存在しないため、直接テーブル操作で実装');
          return await this.createUserProfileDirect(userId, userData);
        }

        logger.error('❌ Function parameters:', {
          user_id: userId,
          user_name: userData.name,
          user_role: userData.role,
          company_id: companyId,
          land_transporter_id: userData.landTransporterId,
        });

        throw new DataServiceError(
          `ユーザープロファイルの作成に失敗しました: ${error.message}`,
          ErrorCodes.DATA_ACCESS_ERROR
        );
      }

      logger.debug('✅ User profile created with custom claims for user:', userId);
    } catch (err) {
      logger.error('❌ createUserProfile例外:', err);

      // ネットワーク関連のエラーや予期しないエラーの場合の代替実装
      if (
        err instanceof Error &&
        (err.message.includes('function') || err.message.includes('network'))
      ) {
        logger.debug('⚠️ エラーのため代替実装を試行');
        return await this.createUserProfileDirect(userId, userData);
      }

      throw err;
    }
  }

  // 代替実装: データベース関数が利用できない場合の直接テーブル操作
  private async createUserProfileDirect(
    userId: string,
    userData: {
      name: string;
      role: Role;
      companyId?: string;
      landTransporterId?: string;
    }
  ): Promise<void> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }

    logger.debug('🔧 直接テーブル操作でユーザープロファイル作成');

    const companyId = userData.role === Role.SUPER_ADMIN ? null : userData.companyId || null;

    // user_profilesテーブルに直接挿入
    const { error: insertError } = await this.supabase.from('user_profiles').insert({
      id: userId,
      name: userData.name,
      role: userData.role,
      company_id: companyId,
      land_transporter_id: userData.landTransporterId || null,
    });

    if (insertError) {
      logger.error('❌ Direct insert error:', insertError);
      throw new DataServiceError(
        `ユーザープロファイルの挿入に失敗しました: ${insertError.message}`,
        ErrorCodes.DATA_ACCESS_ERROR
      );
    }

    logger.debug('✅ User profile created directly for user:', userId);
  }

  // パスワードリセット
  async resetPassword(email: string): Promise<void> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.AUTHENTICATION_FAILED
      );
    }

    try {
      const { error } = await this.supabase.auth.resetPasswordForEmail(email);
      if (error) {
        throw new DataServiceError(
          `パスワードリセットに失敗しました: ${error.message}`,
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }
    } catch (error) {
      logger.error('Password reset error:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'パスワードリセットに失敗しました',
        ErrorCodes.AUTHENTICATION_FAILED,
        error as Error
      );
    }
  }

  // 認証状態監視の設定
  private setupAuthListener(): void {
    if (!this.supabase) return;

    this.supabase.auth.onAuthStateChange(async (event, session) => {
      logger.debug('🔍 Auth state changed:', {
        event,
        userId: session?.user?.id,
        email: session?.user?.email,
        hasCallback: !!this.authStateChangeCallback,
      });

      if (this.authStateChangeCallback) {
        let user: User | null = null;

        if (session?.user) {
          try {
            logger.debug('🔍 Auth listener: ユーザープロファイル取得開始');

            // ユーザープロファイル取得
            const userProfile = await this.getUserProfile(session.user.id);
            user = {
              id: session.user.id,
              name: userProfile.name,
              role: this.mapDatabaseRoleToEnum(userProfile.role),
              company: userProfile.company_id || 'ラクハイ本社',
              companyId: userProfile.company_id,
              landTransporterId: userProfile.land_transporter_id,
            };

            // Auth state change時もlocalStorageに保存（リロード対応）
            try {
              localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(user));
              logger.debug('✅ Auth listener: 認証状態をlocalStorageに保存');
            } catch (storageError) {
              logger.warn('Failed to save auth state change to localStorage:', storageError);
            }
          } catch (error) {
            logger.error('Failed to load user profile on auth state change:', error);
            user = null;

            // ユーザープロファイル読み込み失敗時の通知
            // 実際のアプリケーションでは、ユーザーに適切なエラーメッセージを表示
            if (typeof window !== 'undefined' && window.dispatchEvent) {
              window.dispatchEvent(
                new CustomEvent('auth-profile-error', {
                  detail: { error: error instanceof Error ? error.message : 'Unknown error' },
                })
              );
            }
          }
        } else if (event === 'SIGNED_OUT') {
          // ログアウト時はlocalStorageもクリア
          try {
            localStorage.removeItem(AUTH_STORAGE_KEY);
            logger.debug('✅ Auth listener: ログアウト時にlocalStorageクリア');
          } catch (storageError) {
            logger.warn('Failed to clear localStorage on sign out:', storageError);
          }
        }

        this.authStateChangeCallback(user);
      }
    });
  }

  // 認証状態変更のコールバックを設定（AuthContextから使用）
  onAuthStateChange(callback: (user: User | null) => void): (() => void) | null {
    this.authStateChangeCallback = callback;
    // Supabaseの場合は明示的なunsubscribe関数を返す必要がない（setupAuthListenerで管理）
    return null;
  }

  // セッション取得（デバッグ用）
  async getSession() {
    if (!this.supabase) return null;
    const {
      data: { session },
    } = await this.supabase.auth.getSession();
    return session;
  }

  /**
   * メールアドレス更新（ドライバーロール禁止）
   * 陸送員（Role.DRIVER）はメールアドレスを変更できません
   *
   * @param userId - 対象ユーザーID
   * @param newEmail - 新しいメールアドレス
   * @throws DataServiceError - ドライバーロール、または更新失敗時
   */
  async updateEmail(userId: string, newEmail: string): Promise<void> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.AUTHENTICATION_FAILED
      );
    }

    try {
      // ユーザープロファイル取得
      const userProfile = await this.getUserProfile(userId);

      // ドライバーロールの場合は変更禁止
      if (userProfile.role === Role.DRIVER) {
        throw new DataServiceError(
          '陸送員ユーザーはメールアドレスを変更できません。会社の管理者または配車担当者に変更を依頼してください。',
          ErrorCodes.PERMISSION_DENIED
        );
      }

      // Supabase Auth でメールアドレス更新
      const { error } = await this.supabase.auth.updateUser({
        email: newEmail,
      });

      if (error) {
        throw new DataServiceError(
          `メールアドレスの更新に失敗しました: ${error.message}`,
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      logger.debug('✅ メールアドレス更新成功:', { userId, newEmail });
    } catch (error) {
      logger.error('❌ updateEmail error:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'メールアドレスの更新に失敗しました',
        ErrorCodes.AUTHENTICATION_FAILED,
        error as Error
      );
    }
  }

  /**
   * パスワード更新（ドライバーロール禁止）
   * 陸送員（Role.DRIVER）はパスワードを変更できません
   *
   * @param userId - 対象ユーザーID
   * @param newPassword - 新しいパスワード
   * @throws DataServiceError - ドライバーロール、または更新失敗時
   */
  async updatePassword(userId: string, newPassword: string): Promise<void> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.AUTHENTICATION_FAILED
      );
    }

    try {
      // ユーザープロファイル取得
      const userProfile = await this.getUserProfile(userId);

      // ドライバーロールの場合は変更禁止
      if (userProfile.role === Role.DRIVER) {
        throw new DataServiceError(
          '陸送員ユーザーはパスワードを変更できません。会社の管理者または配車担当者に変更を依頼してください。',
          ErrorCodes.PERMISSION_DENIED
        );
      }

      // Supabase Auth でパスワード更新
      const { error } = await this.supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw new DataServiceError(
          `パスワードの更新に失敗しました: ${error.message}`,
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      logger.debug('✅ パスワード更新成功:', { userId });
    } catch (error) {
      logger.error('❌ updatePassword error:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'パスワードの更新に失敗しました',
        ErrorCodes.AUTHENTICATION_FAILED,
        error as Error
      );
    }
  }

  /**
   * 管理者・配車担当者による他ユーザーのパスワード更新
   * 管理者（Role.ADMIN）と配車担当者（Role.DISPATCHER）のみがこの操作を実行可能
   * 同一会社のユーザーのみ更新可能（スーパー管理者は全ユーザー更新可能）
   *
   * @param authorizedUserId - 操作を実行するユーザーID（管理者・配車担当者）
   * @param targetUserId - パスワード更新対象のユーザーID
   * @param newPassword - 新しいパスワード
   * @throws DataServiceError - 権限不足、会社不一致、または更新失敗時
   */
  async updateUserPasswordByAuthorizedUser(
    authorizedUserId: string,
    targetUserId: string,
    newPassword: string
  ): Promise<void> {
    if (!this.supabase) {
      throw new DataServiceError(
        'Supabaseクライアントが初期化されていません',
        ErrorCodes.AUTHENTICATION_FAILED
      );
    }

    try {
      // 操作実行ユーザーのプロファイル取得
      const authorizedUserProfile = await this.getUserProfile(authorizedUserId);

      // 権限チェック：管理者・配車担当者・スーパー管理者のみ
      if (
        ![Role.SUPER_ADMIN, Role.ADMIN, Role.DISPATCHER].includes(
          authorizedUserProfile.role as Role
        )
      ) {
        throw new DataServiceError(
          'この操作を実行する権限がありません。管理者または配車担当者のみ実行可能です。',
          ErrorCodes.PERMISSION_DENIED
        );
      }

      // 対象ユーザーのプロファイル取得
      const targetUserProfile = await this.getUserProfile(targetUserId);

      // 会社ID一致確認（スーパー管理者はスキップ）
      if (
        authorizedUserProfile.role !== Role.SUPER_ADMIN &&
        authorizedUserProfile.company_id !== targetUserProfile.company_id
      ) {
        throw new DataServiceError(
          '他の会社のユーザーの情報は変更できません',
          ErrorCodes.PERMISSION_DENIED
        );
      }

      // Supabase Admin API でパスワード更新
      // 注: スーパー管理者権限が必要なため、通常ユーザーからは実行不可
      const { error } = await this.supabase.auth.admin.updateUserById(targetUserId, {
        password: newPassword,
      });

      if (error) {
        throw new DataServiceError(
          `ユーザーのパスワード更新に失敗しました: ${error.message}`,
          ErrorCodes.AUTHENTICATION_FAILED
        );
      }

      logger.debug('✅ 管理者によるパスワード更新成功:', {
        authorizedUserId,
        targetUserId,
        authorizedRole: authorizedUserProfile.role,
      });
    } catch (error) {
      logger.error('❌ updateUserPasswordByAuthorizedUser error:', error);
      if (error instanceof DataServiceError) {
        throw error;
      }
      throw new DataServiceError(
        'ユーザーのパスワード更新に失敗しました',
        ErrorCodes.AUTHENTICATION_FAILED,
        error as Error
      );
    }
  }
}
